import styled from "styled-components";

export const C = {
    bg:      "#f8fafc",
    surface: "#ffffff",
    border:  "#e2e8f0",
    text:    "#0f172a",
    muted:   "#64748b",
    indigo:  "#6366f1",
    indigoD: "#4f46e5",
    red:     "#ef4444",
    redBorder: "#fecaca",
    redBg:     "#fef2f2",
    redBgHover:"#fee2e2",
};

export const Shell = styled.div`
    width: 100%;
    max-width: 880px;
    margin: 0 auto;
    padding: 8px 4px 48px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-family: "Inter", system-ui, sans-serif;
`;

export const Banner = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 10px;
    padding: 11px 16px;
    font-size: 0.85rem;
    color: #92400e;
    font-family: "Inter", system-ui, sans-serif;
`;

// Two-column row that collapses to one column on narrow viewports.
export const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    @media (max-width: 620px) {
        grid-template-columns: 1fr;
    }
`;

// $break adds extra top spacing to mark the start of a sub-group inside a
// FormSection — quiet visual separation without a label or rule.
export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    ${({ $break }) => $break && "margin-top: 14px;"}
`;

export const FieldLabel = styled.label`
    font-size: 0.8rem;
    font-weight: 600;
    color: ${C.text};
`;

export const Input = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${({ $invalid }) => $invalid ? C.red : C.border};
    border-radius: 7px;
    font-size: 0.875rem;
    color: ${C.text};
    background: ${({ disabled }) => disabled ? C.bg : C.surface};
    font-family: "Inter", system-ui, sans-serif;
    outline: none;
    box-sizing: border-box;
    &:focus { border-color: ${C.indigo}; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); }
`;

export const Select = styled.select`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${C.border};
    border-radius: 7px;
    font-size: 0.875rem;
    color: ${C.text};
    background: ${({ disabled }) => disabled ? C.bg : C.surface};
    font-family: "Inter", system-ui, sans-serif;
    outline: none;
    box-sizing: border-box;
    &:focus { border-color: ${C.indigo}; }
`;

export const FieldError = styled.div`
    font-size: 0.76rem;
    color: ${C.red};
`;

export const HeaderHint = styled.div`
    font-size: 0.76rem;
    color: ${C.muted};
`;

// Persistent container for the headers editor — stays visible whether or not
// any headers exist; rows and the Add button live inside it.
export const HeadersPane = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 3px;
    padding: 16px;
    border: 1px dashed ${C.border};
    border-radius: 8px;
    background: ${C.bg};
`;

export const HeaderList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const HeaderRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

// Header name is narrower than its value (tokens/URLs run long).
export const HeaderNameInput = styled(Input)`
    flex: 1 1 0;
    min-width: 0;
`;

export const HeaderValueInput = styled(Input)`
    flex: 2 1 0;
    min-width: 0;
`;

export const FlagRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const UsersRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-start;
`;

export const UsersList = styled.select`
    flex: 1;
    height: 96px;
    padding: 6px 10px;
    border: 1px solid ${C.border};
    border-radius: 7px;
    font-size: 0.875rem;
    color: ${C.text};
    font-family: "Inter", system-ui, sans-serif;
    background: ${C.surface};
    outline: none;
    &:focus { border-color: ${C.indigo}; }
`;

export const SmallBtnCol = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const SmallBtn = styled.button`
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
    border: 1px solid ${C.border};
    background: ${C.surface};
    color: ${C.text};
    white-space: nowrap;
    &:hover:not(:disabled) { background: ${C.bg}; }
`;

export const AddHeaderBtn = styled(SmallBtn)`
    align-self: flex-start;
`;

// The action button inside a settings block - Connect, Change, Remove, Rebuild, Wipe. Lighter than
// Btn, which is the page's own Save and Delete.
export const ActionBtn = styled.button`
    padding: 7px 16px;
    border-radius: 7px;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
    transition: background 120ms;
    white-space: nowrap;
    border: ${({ $danger, $secondary }) =>
        $danger ? `1px solid ${C.redBorder}`
        : $secondary ? `1px solid ${C.border}`
        : "none"};
    background: ${({ $danger, $secondary }) =>
        $danger ? C.redBg
        : $secondary ? C.surface
        : C.indigo};
    color: ${({ $danger, $secondary }) =>
        $danger ? C.red
        : $secondary ? C.text
        : "#fff"};
    &:hover:not(:disabled) {
        background: ${({ $danger, $secondary }) =>
            $danger ? C.redBgHover
            : $secondary ? C.bg
            : C.indigoD};
    }
`;

// Centered empty-state content shown inside the headers pane when no headers
// exist yet (the prompt + Add button as one unit).
export const EmptyHeaders = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
`;

export const EmptyHeadersText = styled.div`
    font-size: 0.8rem;
    color: ${C.muted};
`;

export const ToggleLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: ${({ disabled }) => disabled ? "default" : "pointer"};
    user-select: none;
    font-size: 0.875rem;
    color: ${C.text};
    font-family: "Inter", system-ui, sans-serif;
`;

// Inter's visible glyphs sit biased toward the top of their line-box, so plain
// align-items: center makes the text look high next to the toggle track.
// Wrap the inline text in this span to nudge the visual baseline back to the
// optical centre of the toggle.
export const ToggleText = styled.span`
    transform: translateY(1px);
`;

export const ToggleTrack = styled.span`
    position: relative;
    display: inline-block;
    width: 38px;
    height: 22px;
    border-radius: 11px;
    background: ${({ $on }) => $on ? C.indigo : "#cbd5e1"};
    transition: background 150ms;
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${({ $on }) => $on ? "19px" : "3px"};
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        transition: left 150ms;
    }
`;

export const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
`;

// Sticks to the bottom of the viewport while there are unsaved changes so the
// Save button is always reachable — otherwise it's easy to make a change at
// the top of the page and navigate away without scrolling back.
export const FooterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: ${({ $dirty }) => $dirty ? "12px 16px" : "4px 0 0"};
    position: ${({ $dirty }) => $dirty ? "sticky" : "static"};
    bottom: 0;
    background: ${({ $dirty }) => $dirty ? C.surface : "transparent"};
    border: ${({ $dirty }) => $dirty ? `1px solid ${C.border}` : "none"};
    border-radius: ${({ $dirty }) => $dirty ? "10px" : "0"};
    box-shadow: ${({ $dirty }) => $dirty ? "0 -4px 16px rgba(15,23,42,0.06)" : "none"};
    @media (max-width: 620px) {
        flex-direction: column-reverse;
        align-items: stretch;
    }
`;

export const FooterStatus = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
    font-weight: 500;
    color: ${C.muted};
    font-family: "Inter", system-ui, sans-serif;
`;

export const UnsavedDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245,158,11,0.18);
`;

export const Btn = styled.button`
    padding: 10px 22px;
    border-radius: 9px;
    font-size: 0.875rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
    transition: background 120ms, box-shadow 120ms, transform 120ms;
    border: ${({ $danger }) => $danger ? "1px solid #fecaca" : "none"};
    background: ${({ $danger }) => $danger ? "#fff" : C.indigo};
    color: ${({ $danger }) => $danger ? C.red : "#fff"};
    box-shadow: ${({ $danger, $emphasised }) =>
        $danger ? "none"
        : $emphasised ? "0 4px 14px rgba(79,70,229,0.35)"
        : "0 1px 2px rgba(79,70,229,0.25)"};
    &:hover:not(:disabled) {
        background: ${({ $danger }) => $danger ? "#fef2f2" : C.indigoD};
    }
`;
